import React, { useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { TextField } from "@material-ui/core";

import { FlexLayout } from "js/Components/FlexLayout";
import { TextContent } from "js/Components/TextContent";

import { getTaxIdProps } from "../../utils/tax";

const StyledTextField = styled(TextField) <{ warning: boolean }>`
    &&& {
        .MuiInput-root {
            font-size: 16px;
            color: #333;

            &::before {
                border-bottom: 1px solid #e0e0e0;
            }

            &:hover:not(.Mui-disabled):before {
                border-bottom: 1px solid #999;
            }

            &.Mui-focused::after {
                border-bottom: 2px solid #11a9e2;
            }

            &.Mui-error::after {
                border-bottom: 2px solid #f44336;
            }
        }

        .MuiInput-underline:before {
            border-bottom: 1px solid #e0e0e0;
        }

        .MuiInput-underline:hover:not(.Mui-disabled):before {
            border-bottom: 1px solid #999;
        }

        .MuiInput-underline:after {
            border-bottom: 2px solid ${({ warning }) => warning ? "#f44336" : "#11a9e2"};
        }

        .MuiInput-underline.Mui-error:after {
            border-bottom: 2px solid #f44336;
        }

        input {
            color: #333;
            font-size: 16px;
            padding: 6px 0;
        }

        input::placeholder {
            font-style: normal;
            color: #999;
        }

        .MuiFormLabel-root {
            color: #666;
            font-size: 14px;
        }

        .MuiFormLabel-root.Mui-focused {
            color: #11a9e2;
        }

        .MuiFormLabel-root.Mui-error {
            color: #f44336;
        }
    }
`;

interface TaxIdFormProps {
    countryCode: string;
    initialTaxId?: string;
    onChange: (values: { taxId: string, isTaxIdValid: boolean, taxIdType: string | null }) => void;
    disabled?: boolean;
    helperText?: string;
}

export function TaxIdForm({ countryCode, initialTaxId, onChange, disabled, helperText }: TaxIdFormProps) {
    const { t } = useTranslation();
    const [taxId, setTaxId] = useState(initialTaxId ?? "");
    const [showError, setShowError] = useState(false);

    const prevCountryCodeRef = useRef(countryCode);
    useEffect(() => {
        if (prevCountryCodeRef.current !== countryCode) {
            setTaxId("");
        }
        prevCountryCodeRef.current = countryCode;
    }, [countryCode]);

    const taxIdProps = getTaxIdProps(countryCode);
    const isTaxIdValid = taxIdProps.validate(taxId);

    const handleTaxIdChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setTaxId(event.target.value);
    };

    const handleBlur = () => setShowError(true);
    const handleFocus = () => setShowError(false);

    useEffect(() => {
        onChange({
            taxId: taxIdProps.show ? taxId : "",
            taxIdType: taxIdProps.show ? taxIdProps.type : null,
            isTaxIdValid
        });
    }, [taxId, isTaxIdValid, taxIdProps.type, taxIdProps.show]);

    return (
        <FlexLayout visible={taxIdProps.show} direction="column" width="fill">
            <FlexLayout direction="column" width="fill" justify="space-between" align="flex-start">
                <StyledTextField
                    placeholder={taxIdProps.placeholder}
                    onChange={handleTaxIdChange}
                    value={taxId}
                    fullWidth
                    disabled={disabled}
                    autoFocus={false}
                    autoComplete="off"
                    warning={!isTaxIdValid && showError}
                    error={!isTaxIdValid && showError}
                    onBlur={handleBlur}
                    onFocus={handleFocus}
                    data-testid="minimal-billing-address-form-tax-id"
                    label={t("{{name}} (optional)", { name: taxIdProps.name })}
                    variant="standard"
                />
                {(!isTaxIdValid || helperText) && showError && <TextContent errorBlock>{helperText ?? t("Invalid Tax Id")}</TextContent>}
            </FlexLayout>
        </FlexLayout>
    );
}
